import { useMemo } from 'react';
import styled from 'styled-components';
import { useAnalytics } from '../../common/AnalyticsContext/useAnalytics';
import { useSearchFilters } from '../../common/components/SearchHeader/filters/useSearchFilters';
import { SPECIALTY_FLU_VACCINATIONS } from '../../common/components/SearchHeader/types/FilterChoice';
import { FreeTextSearch } from '../../common/schema/hannibal/types.generated';
import { ThemeColours } from '../../common/styles/ThemeColours';
import { Breakpoint, FontSize, FontWeight } from '../../common/styles/constants';
import { returnVoid } from '../../common/util/returnVoid';
import { run } from '../../common/util/run';
import { ProfileCards } from '../ResultView/ResultList/ProfileCards/ProfileCards';
import { ResultCount } from '../ResultView/ResultList/ResultCount/ResultCount';
import { Results } from '../ResultView/ResultList/ResultList.styles';
import { parseProfile } from '../parseProfile';

interface Props {
  primaryResults: string[];
  data: FreeTextSearch;
  fetchMore: VoidFunction;
  fetchingMore: boolean;
}

interface Explanation {
  title: string;
  subtitle?: string;
}

export const ExtendedSearch = ({ primaryResults, data, fetchMore, fetchingMore }: Props) => {
  const [filters] = useSearchFilters();

  const searchSpecialty = data?.searchSpecialty?.name;
  const totalCount = data?.searchProfiles.totalCount ?? 0;

  const extendedResults = useMemo(() => {
    const primarySet = new Set<string>(primaryResults);
    const profiles = (data?.searchProfiles.results ?? []).flatMap(it => parseProfile(it));
    return profiles.filter(it => !primarySet.has(it.id));
  }, [data?.searchProfiles.results, primaryResults]);

  useAnalytics({
    resultsData: {
      extendedDisplayed: extendedResults.length,
      extendedReturned: totalCount,
      totalDisplayed: extendedResults.length + primaryResults.length,
      totalReturned: totalCount + primaryResults.length,
    },
  });

  if (extendedResults.length === 0) {
    return null;
  }

  const explanation: Explanation = run(() => {
    if (filters.topic?.specialist === SPECIALTY_FLU_VACCINATIONS) {
      return {
        title: 'Other practices offering flu vaccination appointments',
        subtitle:
          'These practices offer flu vaccination appointments but they may not be available at your selected time. When booking, please select flu vaccination as the appointment type. If it is not available, please select a different time or practice.',
      };
    }

    return {
      title: 'Appointments for the next available date',
      subtitle: 'These practices may be more than 25km away.',
    };
  });

  return (
    <>
      <Title>{explanation.title}</Title>
      {explanation.subtitle && <Subtitle>{explanation.subtitle}</Subtitle>}
      <Results isLoading={false}>
        <ProfileCards
          profiles={extendedResults}
          searchSpecialty={searchSpecialty}
          isExtendedSearch={true}
          searchSuburb={filters.location?.suburb?.name}
        />
      </Results>
      <ResultCount
        onClickMore={fetchMore ?? returnVoid}
        total={totalCount}
        shown={extendedResults.length}
        fetchingMore={fetchingMore}
        isExtendedSearch={true}
      />
    </>
  );
};

const Title = styled.h2`
  color: ${ThemeColours.mono800};
  font-size: ${FontSize.SMALL};
  margin-bottom: 0;
  margin-top: 0;
  padding-left: 16px;

  ${Breakpoint.SMALL} {
    color: ${ThemeColours.mono700};
    font-size: ${FontSize.HEADING_3_SMALL};
    padding-left: 0;
  }
`;

const Subtitle = styled.h3`
  color: ${ThemeColours.mono600};
  font-size: ${FontSize.SMALL};
  font-weight: ${FontWeight.REGULAR};
  margin-top: 0px;
  padding-left: 16px;

  ${Breakpoint.SMALL} {
    padding-left: 0;
  }
`;
